import { Sentry } from '../config/sentry.config';
import { useAccountStore } from '@/stores/account';
import { watch } from 'vue';
import type { KycCompliance } from '@/types/KycCompliance';
import { session } from '@/helpers/auth';
import '../types/sentry.types';

/**
 * Composable para manejar el contexto del usuario en Sentry
 * Sincroniza automáticamente la información del usuario con Sentry
 * @returns Objeto con funciones para gestionar el contexto del usuario
 */
export function useSentryUser() {
  const accountStore = useAccountStore();

  /**
   * Establece información para usuarios no autenticados
   * Limpia datos anteriores y establece contexto anónimo
   */
  function setUnauthenticatedUser() {
    clearSentryUser();

    Sentry.setUser({
      id: 'anonymous',
      email: 'anonymous@global66.com',
      username: 'Usuario no autenticado',
    });

    Sentry.setTag('user_type', 'b2b');
    Sentry.setTag('is_authenticated', false);
  }

  /**
   * Establece la información del usuario autenticado en Sentry
   * @param kycData - Datos de cumplimiento KYC del usuario
   */
  function setSentryUser(kycData: KycCompliance) {
    if (!session() || !kycData?.b2bUser?.id || !kycData?.company?.id) {
      setUnauthenticatedUser();
      return;
    }

    const { b2bUser, company: { addressCountry, ...company } } = kycData;

    const companyId = company.id?.toString() || 'unknown';
    const currentUserEmail = b2bUser.email;

    if (window.__SENTRY_USER_CACHE__?.id === companyId && 
        window.__SENTRY_USER_CACHE__?.email === currentUserEmail) {
      return;
    }

    clearSentryUser();

    Sentry.setUser({
      id: companyId,
      email: currentUserEmail,
      username: company.name.trim(),
      extra: {
        b2bUser,
        company,
        addressCountry,
      },
    });


    Sentry.setTag('user_type', 'b2b');
    Sentry.setTag('is_authenticated', true);
    
    Sentry.setTag('company_id', company.id || '');

    Sentry.setTag('user_level', b2bUser.level.toString());

    Sentry.setTag('kyc_user_status', b2bUser.kycStage1 || '');
    Sentry.setTag('kyc_company_status', company.kycStage1 || '');
    Sentry.setTag('kyc_company_compliance', company.complianceStatus || '');

    window.__SENTRY_USER_CACHE__ = {
      id: companyId,
      email: currentUserEmail || '',
      timestamp: Date.now()
    };
  }

  /**
   * Limpia completamente la información del usuario en Sentry
   * Elimina usuario, tags y cache
   */
  function clearSentryUser() {
    Sentry.setUser(null);

    Sentry.setTag('user_type', undefined);
    Sentry.setTag('is_authenticated', false);
    Sentry.setTag('company_id', undefined);
    Sentry.setTag('user_level', undefined);

    Sentry.setTag('kyc_user_status', undefined);
    Sentry.setTag('kyc_company_status', undefined);
    Sentry.setTag('kyc_company_compliance', undefined);

    delete window.__SENTRY_USER_CACHE__;
  }

  /**
   * Inicializa el watcher para sincronizar automáticamente el usuario
   * Observa cambios en el estado KYC y actualiza Sentry automáticamente
   */
  function initUserWatcher() {
    watch(
      () => accountStore.kycStatus,
      (newKycStatus, oldKycStatus) => {
        if (newKycStatus === oldKycStatus) return;

        if (!session()) {
          setUnauthenticatedUser();
          return;
        }

        const hasValidUserData = newKycStatus?.b2bUser?.id && newKycStatus?.company?.id;
        
        if (!hasValidUserData) {
          setUnauthenticatedUser();
          return;
        }

        setSentryUser(newKycStatus);
      },
      { immediate: true }
    );
  }

  return {
    setSentryUser,
    setUnauthenticatedUser,
    clearSentryUser,
    initUserWatcher,
  };
}
